import {
  Identifiable,
  OptimizedGantt,
  Project,
  project,
  ProjectElementType,
  WorkLoadsCalendar,
} from "@planzone/dto";
import { DateTime } from "luxon";
import React from "react";
import { useProjectResourcesRetrieval } from "@planzone/view-models";
import { useWorkspaceContext } from "@scenes/workspaceContext";
import styles from "./styles.scss";

function getDates(minDateIso: string, maxDateIso: string): Date[] {
  // Returns dates from (minDate + 1 day) through (maxDate + 1 day) at
  // local-midnight granularity. The +1 offset is intentional and is
  // compensated by the parent's ±1 month pre-shift in index.tsx — see
  // workloads-date-audit.md §12. Do NOT collapse this off-by-one without
  // also updating the parent compensation; the two are a single contract.
  //
  // The pre-migration implementation mixed a millisecond-tick loop bound
  // with calendar-day construction, which produced subtly different
  // results across DST transitions. The Luxon version uses calendar-day
  // arithmetic consistently — same output under all non-DST conditions,
  // and well-defined under DST.
  const min = DateTime.fromISO(minDateIso);
  const max = DateTime.fromISO(maxDateIso);
  if (!min.isValid || !max.isValid) return [];

  const dates: Date[] = [];
  const limit = max.plus({ days: 1 }).startOf("day");
  let cursor = min.plus({ days: 1 }).startOf("day");
  while (cursor <= limit) {
    dates.push(cursor.toJSDate());
    cursor = cursor.plus({ days: 1 });
  }
  return dates;
}

function reCalculateNodeDateRange(
  projectElement: OptimizedGantt[number],
  minDate: DateTime,
  maxDate: DateTime,
): { startDate: DateTime; endDate: DateTime } {
  return {
    startDate: projectElement.startDate
      ? DateTime.fromISO(projectElement.startDate, { zone: "utc" })
      : minDate,
    endDate: projectElement.endDate
      ? DateTime.fromISO(projectElement.endDate, { zone: "utc" })
      : maxDate,
  };
}

function reCalculateLeafDateRange(
  projectElement: OptimizedGantt[number],
  minDate: DateTime,
  maxDate: DateTime,
): { startDate: DateTime; endDate: DateTime } {
  const endDate = projectElement.endDate
    ? DateTime.fromISO(projectElement.endDate, { zone: "utc" })
    : maxDate;

  return {
    startDate: projectElement.startDate
      ? DateTime.fromISO(projectElement.startDate, { zone: "utc" })
      : projectElement.endDate
        ? // FIXME: timezone bug — same shape as WBS utils.ts:184 and Gantt
          // utils.ts — separate ticket. The derived startDate uses local-TZ
          // day of the source endDate, producing different output across
          // browsers. Preserved here to match the audit; cleanup deferred.
          // `.toLocal()` matches the pre-migration `DateTime.fromJSDate(...)`
          // step which interpreted the UTC Date in browser-local TZ before
          // snapping to startOf("day").
          endDate.toLocal().startOf("day")
        : maxDate.toLocal().startOf("day"),
    endDate,
  };
}

function reCalculateDateRange(
  projectElement: OptimizedGantt[number],
  minDate: DateTime,
  maxDate: DateTime,
): { startDate: DateTime; endDate: DateTime } {
  return projectElement.type === ProjectElementType.LIST ||
    projectElement.type === ProjectElementType.PROJECT
    ? reCalculateNodeDateRange(projectElement, minDate, maxDate)
    : reCalculateLeafDateRange(projectElement, minDate, maxDate);
}

function renderWithTreeCtx(
  gantt: OptimizedGantt,
  isOpen: (key: string) => boolean,
  workLoad: WorkLoadsCalendar[number],
  render: (
    projectElement: OptimizedGantt[number],
    ctx: { minDate: string; maxDate: string },
    index: number,
    fullCtx: {
      [key: number]: { minDate: string; maxDate: string };
    },
  ) => React.JSX.Element,
): React.ReactNode[] {
  return gantt.reduce<{
    ui: React.JSX.Element[];
    filteredCount: number;
    ctx: {
      [key: number]: {
        minDate: string;
        maxDate: string;
        filtered: boolean;
      };
    };
  }>(
    ({ ui, ctx, filteredCount }, projectElement, index) => {
      const isChangingContext =
        projectElement.type === ProjectElementType.PROJECT;
      const isChangingDepth =
        projectElement.type === ProjectElementType.LIST &&
        gantt[index + 1]?.depth !== projectElement.depth;
      const filtered = ctx[projectElement.depth - 1]?.filtered;

      return {
        filteredCount: (
          isChangingContext
            ? !!projectElement.startDate && !!projectElement.endDate
            : !filtered
        )
          ? filteredCount
          : filteredCount + 1,
        ui: (
          isChangingContext
            ? !!projectElement.startDate && !!projectElement.endDate
            : !filtered
        )
          ? [
              ...ui,
              render(
                projectElement,
                ctx[
                  projectElement.depth - 1 > 0 ? projectElement.depth - 1 : 0
                ],
                index - filteredCount,
                ctx,
              ),
            ]
          : ui,
        ctx: isChangingContext
          ? {
              0: {
                minDate: projectElement.startDate,
                maxDate: projectElement.endDate,
                filtered:
                  !projectElement.startDate ||
                  !projectElement.endDate ||
                  !isOpen(`${workLoad.uuid}:${projectElement.uuid}`),
              },
            }
          : isChangingDepth
            ? {
                ...ctx,
                [projectElement.depth]: {
                  filtered:
                    filtered ||
                    !isOpen(`${workLoad.uuid}:${projectElement.uuid}`),
                  minDate:
                    projectElement.startDate ||
                    ctx[projectElement.depth]?.minDate ||
                    ctx[projectElement.depth - 1]?.minDate ||
                    "",
                  maxDate:
                    projectElement.endDate ||
                    ctx[projectElement.depth]?.maxDate ||
                    ctx[projectElement.depth - 1]?.maxDate ||
                    "",
                },
              }
            : ctx,
      };
    },
    {
      filteredCount: 0,
      ui: [],
      ctx: {
        0: {
          minDate: gantt[0]?.startDate,
          maxDate: gantt[0]?.endDate,
          filtered: !gantt[0]?.startDate || !gantt[0]?.endDate,
        },
      },
    },
  ).ui;
}

// Inside a project, the workloads of its members. Elsewhere no resource is given: the back computes the
// workloads of every planzone resource, so the whole list is not loaded for its uuids.
function useResources() {
  const ctx = useWorkspaceContext();
  const isInProject = ctx.type === "project";
  const { projectResources } = useProjectResourcesRetrieval(
    isInProject ? ctx.project : { ...project, uuid: "" },
  );

  return {
    resources: isInProject
      ? projectResources.map(({ resource }) => resource)
      : [],
  };
}

function isToday(date: Date) {
  return DateTime.fromJSDate(date).hasSame(DateTime.now(), "day");
}

function useRange(offset: number, edit: (data: number) => void) {
  const rangeRef = React.useRef<HTMLDivElement>(null);
  const [canMove, setCanMove] = React.useState<boolean>(false);
  const hasSelection = () => canMove;
  const resetSelection = () => setCanMove(false);
  const selectThumb = () => setCanMove(true);
  const update = (nextValue: number) => edit(nextValue);
  const getNextMoveValue = (rangeElement: HTMLDivElement, offset: number) => {
    const rect = rangeElement.getBoundingClientRect();
    const nextValue = Math.round(offset - rect.left);
    const rangeWidth = parseFloat(
      window.getComputedStyle(rangeElement).width as string,
    );

    return Math.round((nextValue / rangeWidth) * 100);
  };
  const canUpdate = (offset: number) => {
    const isBelowLimit = offset <= 100;
    const isAboveLimit = offset >= 0;

    return isBelowLimit && isAboveLimit;
  };

  React.useEffect(() => {
    const cancelSelection = () => {
      hasSelection() && resetSelection();
      document.documentElement.classList.remove(styles.unselectable);
    };
    const dispatchChange = (e: MouseEvent | TouchEvent) => {
      const mouseEvent = e as MouseEvent;
      const touchEvent = e as TouchEvent;

      hasSelection() &&
        !document.documentElement.classList.contains(styles.unselectable) &&
        document.documentElement.classList.add(styles.unselectable);

      if (rangeRef.current) {
        const nextValue = getNextMoveValue(
          rangeRef.current,
          !!mouseEvent.clientX
            ? mouseEvent.clientX
            : touchEvent.changedTouches.length > 0
              ? touchEvent.changedTouches[0].clientX
              : 0,
        );

        hasSelection() && canUpdate(nextValue) && update(nextValue);
      }
    };

    window.addEventListener("touchmove", dispatchChange);
    window.addEventListener("mousemove", dispatchChange);
    window.addEventListener("mouseup", cancelSelection);
    window.addEventListener("touchend", cancelSelection);

    return () => {
      window.removeEventListener("touchmove", dispatchChange);
      window.removeEventListener("mousemove", dispatchChange);
      window.removeEventListener("mouseup", cancelSelection);
      window.removeEventListener("touchend", cancelSelection);
    };
  });

  return { rangeRef, selectThumb };
}

export {
  getDates,
  reCalculateDateRange,
  renderWithTreeCtx,
  useResources,
  isToday,
  useRange,
};
